import { CamIcon } from '@daily/shared/components/Icons/CamIcon';
import { HiddenIcon } from '@daily/shared/components/Icons/HiddenIcon';
import { LowerHandIcon } from '@daily/shared/components/Icons/LowerHandIcon';
import { MicrophoneIcon } from '@daily/shared/components/Icons/MicrophoneIcon';
import { PinIcon } from '@daily/shared/components/Icons/PinIcon';
import { StopScreenshareIcon } from '@daily/shared/components/Icons/StopScreenshareIcon';
import { Menu } from '@daily/shared/components/Menu';
import { Text } from '@daily/shared/components/Text';
import { useTheme } from '@daily/shared/contexts/Theme';
import {
  useDaily,
  useLocalSessionId,
  useParticipantIds,
  useParticipantProperty,
  useScreenShare,
} from '@daily-co/daily-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useBreakoutSessionState } from '/components/BreakoutRooms/hooks/useBreakoutSessionState';
import { useCallState } from '/contexts/CallProvider';
import { useParticipantMarkedForRemoval } from '/contexts/ParticipantsProvider';
import { useCallConfig } from '/hooks/useCallConfig';
import { useForceLowerHand, useHasHandRaised } from '/hooks/useHandRaising';
import { useScreens } from '/hooks/useScreens';
import { useStyleVariants } from '/hooks/useStyleVariants';
import { useIsMobile } from '/lib/state/core';
import { useShowLocalVideo, useShowUserNameChangeUI } from '/lib/state/custom';
import { usePinnedId } from '/lib/state/layout';
import { useNameModal, useSettingsView } from '/lib/state/modals';

type MenuPropsWithoutItems = Omit<
  React.ComponentProps<typeof Menu>,
  'children' | 'items'
>;

interface Props extends MenuPropsWithoutItems {
  sessionId: string;
}

export const ParticipantMenu: React.FC<Props> = ({
  onClose,
  sessionId,
  ...menuProps
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { disableAudio } = useCallState();
  const [showUserNameChangeUI] = useShowUserNameChangeUI();
  const [showLocalVideo, setShowLocalVideo] = useShowLocalVideo();
  const { broadcast, broadcastRole, enableVideoProcessingUI } = useCallConfig();
  const daily = useDaily();
  const [, setParticipantMarkedForRemoval] = useParticipantMarkedForRemoval();
  const { forceLowerHand } = useForceLowerHand();
  const localSessionId = useLocalSessionId();
  const [isLocalOwner, isLocalVideoEnabled] = useParticipantProperty(
    localSessionId,
    ['owner', 'video']
  );

  const {
    breakoutParticipantIds,
    breakoutStatus,
    hasJoinedBreakout,
    mainRoomParticipantIds,
  } = useBreakoutSessionState();

  const filteredScreens = useScreens();
  const filteredScreen = useMemo(
    () => filteredScreens.find((s) => s.session_id === sessionId),
    [filteredScreens, sessionId]
  );

  // Grab all screens across breakout rooms so owners are able to stop them
  const { screens } = useScreenShare();
  const screen = useMemo(
    () => screens.find((s) => s.session_id === sessionId),
    [screens, sessionId]
  );
  const [isMobile] = useIsMobile();
  const [, setShowNameModal] = useNameModal();
  const [pinnedId, setPinnedId] = usePinnedId();
  const [, setSettingsView] = useSettingsView();
  const { iconVariant, textVariant } = useStyleVariants();

  const participantIds = useParticipantIds();
  const [hasAudioEnabled, hasVideoEnabled, isOwner] = useParticipantProperty(
    sessionId,
    ['audio', 'video', 'owner']
  );
  const isLocal = sessionId === localSessionId;
  const userHasHandRaised = useHasHandRaised(sessionId);

  const pinLabel = useMemo(() => {
    const isPinned = pinnedId === sessionId;
    if (isLocal) {
      return isPinned ? t('people.unpinYourself') : t('people.pinYourself');
    }
    return isPinned ? t('people.unpinParticipant') : t('people.pinParticipant');
  }, [isLocal, sessionId, pinnedId, t]);

  const handlePinClick = useCallback(() => {
    setPinnedId(sessionId);
    onClose?.();
  }, [onClose, sessionId, setPinnedId]);
  const handleUnpinClick = () => {
    setPinnedId(null);
    onClose?.();
  };

  const handleRenameClick = () => {
    setShowNameModal(true);
    onClose?.();
  };

  const handleBackgroundEffectsClick = () => {
    setSettingsView('effects');
    onClose?.();
  };

  const handleToggleSelfViewClick = () => {
    setShowLocalVideo(!showLocalVideo);
    onClose?.();
  };

  const handleMicMuteClick = () => {
    if (isLocal) {
      daily.setLocalAudio(!hasAudioEnabled);
    } else {
      daily.updateParticipant(sessionId, { setAudio: false });
    }
    onClose?.();
  };

  const handleCamMuteClick = () => {
    if (isLocal) {
      daily.setLocalVideo(!hasVideoEnabled);
    } else {
      daily.updateParticipant(sessionId, { setVideo: false });
    }
    onClose?.();
  };

  const handleStopScreenShareClick = () => {
    daily.sendAppMessage('stop-screen-share', sessionId);
    onClose?.();
  };

  const handleLowerHandByHost = useCallback(() => {
    forceLowerHand(sessionId);
    onClose?.();
  }, [forceLowerHand, onClose, sessionId]);

  const handleRemoveClick = () => {
    setParticipantMarkedForRemoval(sessionId);
    onClose?.();
  };

  const menuItems: React.ComponentProps<typeof Menu>['items'] = [];

  // Rename
  if (isLocal) {
    menuItems.push(
      ...(showUserNameChangeUI
        ? [
            {
              label: <Text variant={textVariant}>{t('people.rename')}</Text>,
              onClick: handleRenameClick,
            },
          ]
        : []),
      ...(enableVideoProcessingUI && !isMobile && broadcastRole !== 'attendee'
        ? [
            {
              disabled: !isLocalVideoEnabled,
              label: (
                <Text
                  color={!isLocalVideoEnabled ? 'muted' : 'default'}
                  variant={textVariant}
                >
                  {t('video.backgroundEffects')}
                </Text>
              ),
              onClick: handleBackgroundEffectsClick,
            },
          ]
        : []),
      ...(broadcastRole !== 'attendee'
        ? [
            {
              disabled: participantIds.length === 1,
              icon: <HiddenIcon size={iconVariant} />,
              label: (
                <Text
                  color={participantIds.length === 1 ? 'muted' : 'default'}
                  variant={textVariant}
                >
                  {showLocalVideo
                    ? t('people.hideSelfView')
                    : t('people.showSelfView')}
                </Text>
              ),
              onClick: handleToggleSelfViewClick,
            },
          ]
        : [])
    );
  }

  const isBroadcastOwner = broadcast && isLocalOwner && isOwner;
  const hasMutePermission = isLocalOwner && !isLocal;
  /**
   * Mute/Unmute microphone and camera
   * In OOB calls, attendees can't turn on their cam/mic so muting
   * doesn't need to be offered here.
   */
  if (isBroadcastOwner || (!broadcast && hasMutePermission)) {
    if (!disableAudio) {
      menuItems.push({
        disabled: !hasAudioEnabled,
        label: (
          <Text
            color={!hasAudioEnabled ? 'muted' : 'default'}
            variant={textVariant}
          >
            {t('audio.muteMicrophone')}
          </Text>
        ),
        icon: (
          <MicrophoneIcon
            color={!hasAudioEnabled ? colors.supportiveText : 'currentColor'}
            muted
            size={iconVariant}
          />
        ),
        onClick: handleMicMuteClick,
      });
    }
    menuItems.push({
      disabled: !hasVideoEnabled,
      label: (
        <Text
          color={!hasVideoEnabled ? 'muted' : 'default'}
          variant={textVariant}
        >
          {t('video.muteCamera')}
        </Text>
      ),
      icon: (
        <CamIcon
          id={`mute-cam-${sessionId}`}
          color={!hasVideoEnabled ? colors.supportiveText : 'currentColor'}
          muted
          size={iconVariant}
          transition={false}
        />
      ),
      onClick: handleCamMuteClick,
    });
  }

  // Allow host to lower a participant's hand
  if (isLocalOwner && !isLocal && userHasHandRaised) {
    menuItems.push({
      label: <Text variant={textVariant}>{t('handRaising.lowerHand')}</Text>,
      icon: <LowerHandIcon size={iconVariant} />,
      onClick: handleLowerHandByHost,
    });
  }

  // Stop screen share
  if (isLocalOwner && !isLocal && screen) {
    menuItems.push({
      label: <Text variant={textVariant}>{t('screenShare.stopShare')}</Text>,
      icon: <StopScreenshareIcon size={iconVariant} />,
      onClick: handleStopScreenShareClick,
    });
  }

  const isBroadcastNonAttendee = broadcast && isOwner;
  const isSameBreakoutRoom =
    breakoutStatus === 'ongoing'
      ? hasJoinedBreakout
        ? breakoutParticipantIds.includes(sessionId)
        : mainRoomParticipantIds.includes(sessionId)
      : true;
  // In OOB calls, pinning is only available for owner tiles
  // Pinning is never available on mobile
  // In Breakout scenarios, pinning is only available for participants in the same room
  if (
    !isMobile &&
    isSameBreakoutRoom &&
    (!broadcast || isBroadcastNonAttendee)
  ) {
    const disabled = isLocal && !showLocalVideo;
    // Pin / Unpin
    menuItems.push({
      disabled,
      label: (
        <Text color={disabled ? 'muted' : 'default'} variant={textVariant}>
          {pinLabel}
        </Text>
      ),
      icon: <PinIcon size={iconVariant} />,
      onClick:
        pinnedId === sessionId || pinnedId === filteredScreen?.screenId
          ? handleUnpinClick
          : handlePinClick,
    });
  }

  // Remove from call
  if (isLocalOwner && !isLocal) {
    menuItems.push({
      label: (
        <Text color="error" variant={textVariant}>
          {t('people.removeFromCall')}
        </Text>
      ),
      onClick: handleRemoveClick,
    });
  }

  if (menuItems.length === 0) return null;

  return <Menu items={menuItems} onClose={onClose} {...menuProps} />;
};
